import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export const PrivacyPolicyPage = () => {
  const lastUpdated = 'February 12, 2026';
  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-gray-200 bg-white/80 backdrop-blur-lg sticky top-0 z-30">
        <div className="max-w-screen-md mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="privacy-back-home">
            RealProp
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-black transition-colors" data-testid="privacy-back-link">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
        </div>
      </header>

      <main className="max-w-screen-md mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="privacy-title">
          Privacy Policy
        </h1>
        <p className="text-sm text-gray-500 mb-10" style={{ fontFamily: 'Manrope, sans-serif' }}>Last updated: {lastUpdated}</p>

        <div className="prose prose-gray max-w-none space-y-8" style={{ fontFamily: 'Manrope, sans-serif' }}>

          <section>
            <h2 className="text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>1. Introduction</h2>
            <p className="text-gray-700 leading-relaxed">
              RealProp ("we", "us", or "our") provides AI-powered marketing tools for real estate professionals. This Privacy Policy explains how we collect, use, store, and share information when you use our website at <a href="https://realprop.io" className="text-violet-600 underline">https://realprop.io</a> and related services (collectively, the "Service").
            </p>
            <p className="text-gray-700 leading-relaxed mt-3">
              By using the Service you agree to this Privacy Policy. If you do not agree, please do not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>2. Information We Collect</h2>
            <h3 className="font-semibold mt-4 mb-1">Information you provide</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li>Account info: name, email address, password (hashed), brokerage name, profile photo.</li>
              <li>Brand kit: logos, colors, fonts, and contact details you upload.</li>
              <li>Listing data: property addresses, prices, photos, descriptions, and any links you provide.</li>
              <li>Billing info: handled directly by PayPal — we never see or store your card details.</li>
              <li>Communications: support emails or messages you send us.</li>
            </ul>
            <h3 className="font-semibold mt-4 mb-1">Information we collect automatically</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li>Usage data: pages viewed, features used, posts created, scrapes performed.</li>
              <li>Device data: IP address, browser type, operating system, time zone.</li>
              <li>Cookies and similar technologies for session management and analytics.</li>
            </ul>
            <h3 className="font-semibold mt-4 mb-1">Information from third parties</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li><strong>Apify / Zillow / Realtor.com</strong>: When you paste a public listing URL, we use Apify (a third-party scraping service) to fetch public property details from real estate websites.</li>
              <li><strong>Google / Meta</strong>: If you connect your Google or Meta (Facebook/Instagram) account, we receive the data those providers share according to your authorization.</li>
              <li><strong>AI providers</strong>: We send property data to Emergent's hosted LLM endpoints (OpenAI, Anthropic, Google) to generate captions, descriptions, and images.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>3. How We Use Your Information</h2>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li>Operate, maintain, and improve the Service.</li>
              <li>Generate AI content (captions, descriptions, images, video reels) based on the listing data you provide.</li>
              <li>Publish content to your connected social accounts when you authorize it.</li>
              <li>Process subscription billing via PayPal.</li>
              <li>Enforce plan limits (post counts, scrape counts, etc.).</li>
              <li>Send transactional and marketing emails (you can opt out of marketing at any time).</li>
              <li>Detect, prevent, and investigate fraud or abuse.</li>
              <li>Comply with legal obligations.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>4. Sharing Your Information</h2>
            <p className="text-gray-700 leading-relaxed mb-3">We do not sell your personal information. We share information only with:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li><strong>Service providers</strong> who help us run the Service, including:
                <ul className="list-circle pl-6 mt-1 space-y-1">
                  <li>Cloud hosting and infrastructure (Emergent)</li>
                  <li>Database and storage (MongoDB, Supabase)</li>
                  <li>Payments (PayPal)</li>
                  <li>AI providers (OpenAI, Anthropic, Google via Emergent LLM Key)</li>
                  <li>Web scraping (Apify)</li>
                  <li>Email delivery (Resend)</li>
                </ul>
              </li>
              <li><strong>Social platforms</strong> (Meta/Instagram, etc.) when you choose to publish content to them.</li>
              <li><strong>Authorities</strong> when required by law, subpoena, or to protect rights and safety.</li>
              <li><strong>Acquirers</strong> in connection with a merger, acquisition, or sale of assets — we will notify you before your data is transferred.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>5. Data Retention</h2>
            <p className="text-gray-700 leading-relaxed">
              We retain your account information for as long as your account is active. When you delete your account, we delete or anonymize your personal data within 30 days, except where retention is required by law (e.g., tax records) or for legitimate business interests (e.g., fraud prevention).
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>6. Your Rights</h2>
            <p className="text-gray-700 leading-relaxed mb-3">Depending on your jurisdiction, you may have the right to:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li>Access the personal data we hold about you</li>
              <li>Correct inaccurate data</li>
              <li>Delete your data ("right to be forgotten")</li>
              <li>Export your data in a portable format</li>
              <li>Object to or restrict certain processing</li>
              <li>Withdraw consent at any time</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-3">
              To exercise any of these rights, email us at <a href="mailto:support@realprop.io" className="text-violet-600 underline">support@realprop.io</a>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>7. Cookies</h2>
            <p className="text-gray-700 leading-relaxed">
              We use first-party cookies and similar technologies to keep you signed in, remember your preferences, and analyze usage. You can disable cookies in your browser, but some features may not work correctly.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>8. Security</h2>
            <p className="text-gray-700 leading-relaxed">
              We implement industry-standard safeguards (encryption in transit, encrypted password hashing, access controls) to protect your data. No system is 100% secure, however, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>9. International Transfers</h2>
            <p className="text-gray-700 leading-relaxed">
              Your data may be processed in the United States and other countries where our service providers operate. By using the Service you consent to such transfers.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>10. Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              The Service is not directed to children under 16, and we do not knowingly collect personal data from them. If you believe a child has provided us data, please contact us so we can delete it.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>11. Changes to This Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Privacy Policy from time to time. Material changes will be notified via email or an in-app notice. The "Last updated" date at the top of this page indicates when the policy was last revised.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>12. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed">
              Questions? Email us at <a href="mailto:support@realprop.io" className="text-violet-600 underline">support@realprop.io</a>.
            </p>
          </section>

        </div>

        <div className="mt-16 pt-8 border-t border-gray-200 text-center text-sm text-gray-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <Link to="/terms" className="text-violet-600 underline mr-4" data-testid="link-to-terms">Terms of Service</Link>
          <Link to="/data-deletion" className="text-violet-600 underline mr-4" data-testid="link-to-data-deletion">Data Deletion</Link>
          <Link to="/" className="text-violet-600 underline">Home</Link>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicyPage;
